/**
 * Demo Center component status (WQ-348, DC-17): GET /api/demos/status.
 *
 * Every entitled user gets full detail: per component, state, version, build time,
 * the failing check and the last deploy. A non-JSON answer (e.g. CloudFront's HTML 200
 * for an origin 403/404, DEF-090) is never trusted. `not_live`: the component isn't
 * deployed yet (before go-live), shown neutrally with no tile badge.
 */

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

export type ComponentState = 'ok' | 'degraded' | 'down' | 'not_live';

export interface ComponentCheck {
  name: string;
  ok: boolean;
  detail: string | null;
}

export interface StoreStats {
  available: boolean;
  bytes?: number;
  uploads?: number;
  sites?: number;
  archived?: number;
  as_of?: string | null;
  error?: string | null;
}

export interface DemoComponent {
  id: string;
  label: string;
  state: ComponentState;
  version: { sha: string; built_at: string | null } | null;
  checks: ComponentCheck[];
  detail: string | null;
  store?: StoreStats;
}

export interface DemoStatus {
  entitled: true;
  checked_at: string;
  components: DemoComponent[];
  deploy: { sha: string | null; finished_at: string | null; conclusion: string | null; run_url: string | null } | null;
  host: { disk_free_pct: number | null };
}

export type DemoStatusResult =
  | { kind: 'ok'; status: DemoStatus }
  | { kind: 'not-entitled' }
  | { kind: 'error' };

/** Demo mount (/demos/<mount>/, slug as fallback) → the server components its live features need.
 *  One constant, no manifest schema change. */
export const DEMO_COMPONENTS: Record<string, string[]> = {
  // SENTINEL's Mine Surveys / Add mine run on the CAD ingest service (WQ-348, DC-14).
  sentinel: ['cad-ingest'],
};

const STATES: readonly string[] = ['ok', 'degraded', 'down', 'not_live'];

function isStatus(body: unknown): body is DemoStatus {
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  return b.entitled === true && Array.isArray(b.components)
    && b.components.every((c) => !!c && typeof c === 'object'
      && typeof (c as DemoComponent).id === 'string' && STATES.includes((c as DemoComponent).state));
}

export async function getDemoStatus(authFetch: AuthFetch): Promise<DemoStatusResult> {
  try {
    const res = await authFetch('/api/demos/status');
    if (!res.ok || !(res.headers.get('content-type') || '').includes('application/json')) return { kind: 'error' };
    const body: unknown = await res.json();
    if (body && typeof body === 'object' && (body as { entitled?: unknown }).entitled === false) {
      return { kind: 'not-entitled' };
    }
    return isStatus(body) ? { kind: 'ok', status: body } : { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

export function shortSha(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 7) : '—';
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}
